import { format } from 'date-fns';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { McpOAuthGrant } from '@/api/v4/mcp-oauth.api';
import type { UserApiKey } from '@/api/v4/user-api-key.api';
import { Loader } from '@/components/loader/loader';
import { modalState } from '@/state/modal.state';
import { mediaQuery } from '@/theme/setupTheme';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';

import { PRODUCT } from '@/user/products/product.type';
import { useUserProducts } from '@/user/products/use-user-products';

import { ClaudeMcpConnectorCard } from './claude-mcp-connector/claude-mcp-connector-card';
import { useDeleteUserApiKey } from './user-api-keys/use-delete-user-api-key.mutation';
import { useMcpOAuthGrants } from './mcp-oauth/use-mcp-oauth-grants.query';
import { useRevokeMcpOAuthGrant } from './mcp-oauth/use-revoke-mcp-oauth-grant.mutation';
import { useUserApiKeys } from './user-api-keys/use-user-api-keys.query';

export const ApiAndIntegrations = () => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const setModal = useSetAtom(modalState);
  const { data: keys = [], isLoading: isListLoading } = useUserApiKeys();
  const { data: mcpGrants = [], isLoading: isMcpGrantsLoading } =
    useMcpOAuthGrants();
  const { deleteToken } = useDeleteUserApiKey();
  const { revokeGrant } = useRevokeMcpOAuthGrant();
  const { userProducts } = useUserProducts();
  const hasClaudeMcpConnector = userProducts.includes(
    PRODUCT.CLAUDE_MCP_CONNECTOR,
  );
  const hasPersonalAccessToken = userProducts.includes(
    PRODUCT.PERSONAL_ACCESS_TOKEN,
  );
  const claudeGrant = mcpGrants[0] ?? null;
  const hasApiKeys = keys.length > 0;

  const openCreateModal = (): void => {
    if (!hasPersonalAccessToken) {
      return;
    }
    setModal({ state: 'createAccessToken' });
  };

  const openDisconnectMcpModal = (grant: McpOAuthGrant): void => {
    if (!hasPersonalAccessToken) {
      return;
    }
    setModal({
      confirmLabel: t`mcpOAuth.disconnectConfirm`,
      description: t`mcpOAuth.disconnectDescription`,
      onConfirm: () => {
        void revokeGrant(grant.id);
      },
      state: 'confirmDelete',
      title: t`mcpOAuth.disconnectTitle`,
    });
  };

  const openRevokeModal = (key: UserApiKey): void => {
    if (!hasPersonalAccessToken) {
      return;
    }
    setModal({
      confirmLabel: t`delete.confirmButton`,
      description: t`delete.description`,
      onConfirm: () => {
        void deleteToken(key.id);
      },
      state: 'confirmDelete',
      title: t`delete.title`,
    });
  };

  return (
    <ApiAndIntegrationsContainer>
      <Stack fullWidth gap="32px">
        <Stack fullWidth gap="8px">
          <SectionTitle>{t`title`}</SectionTitle>
          <SectionDescription>{t`description`}</SectionDescription>
        </Stack>

        <Stack fullWidth gap="16px">
          <ApiKeysCard fullWidth gap="16px">
            <Flexbox alignItems="center" fullWidth justify="flex-end">
              <Button
                onClick={openCreateModal}
                startIcon="Plus"
                type="button"
                variant="secondary"
              >
                {t`createToken`}
              </Button>
            </Flexbox>

            {isListLoading ? (
              <Flexbox justify="center" padding="24px 0" fullWidth>
                <Loader />
              </Flexbox>
            ) : hasApiKeys ? (
              <Stack fullWidth gap="12px">
                <TableHeaderRow
                  alignItems="center"
                  fullWidth
                  gap="16px"
                  wrap="wrap"
                >
                  <HeaderCell $minWidth="120px">{t`table.name`}</HeaderCell>
                  <HeaderCell $flex={1} $minWidth="160px">
                    {t`table.token`}
                  </HeaderCell>
                  <HeaderCell $minWidth="100px">{t`table.created`}</HeaderCell>
                  <HeaderCell $minWidth="100px">{t`table.lastUsed`}</HeaderCell>
                  <HeaderCell $minWidth="100px">{t`table.expires`}</HeaderCell>
                  <HeaderCell $minWidth="80px">{t`table.actions`}</HeaderCell>
                </TableHeaderRow>

                {keys.map(key => (
                  <DataRow
                    alignItems="center"
                    fullWidth
                    gap="16px"
                    key={key.id}
                    wrap="wrap"
                  >
                    <DataCell $minWidth="120px">{key.name}</DataCell>
                    <MonoCell $flex={1} $minWidth="160px">
                      {key.preview}
                    </MonoCell>
                    <DataCell $minWidth="100px">
                      {format(new Date(key.createdAt), 'MMM d, yyyy')}
                    </DataCell>
                    <DataCell $minWidth="100px">
                      {key.lastUsedAt
                        ? format(new Date(key.lastUsedAt), 'MMM d, yyyy')
                        : t`table.neverUsed`}
                    </DataCell>
                    <DataCell $minWidth="100px">
                      {key.expiresAt
                        ? format(new Date(key.expiresAt), 'MMM d, yyyy')
                        : t`table.noExpiration`}
                    </DataCell>
                    <DataCell $minWidth="80px">
                      <Button
                        onClick={() => openRevokeModal(key)}
                        type="button"
                        variant="ghost"
                      >
                        {t`delete.confirmButton`}
                      </Button>
                    </DataCell>
                  </DataRow>
                ))}
              </Stack>
            ) : (
              <EmptyState>{t`emptyState`}</EmptyState>
            )}
          </ApiKeysCard>
        </Stack>

        {hasClaudeMcpConnector ? (
          <Stack fullWidth gap="16px">
            <Stack fullWidth gap="8px">
              <SectionTitle>{t`connectedApps.title`}</SectionTitle>
              <SectionDescription>
                {t`connectedApps.description`}
              </SectionDescription>
            </Stack>

            <ConnectorsGrid>
              <ClaudeMcpConnectorCard
                grant={claudeGrant}
                isLoading={isMcpGrantsLoading}
                onDisconnect={openDisconnectMcpModal}
              />
            </ConnectorsGrid>
          </Stack>
        ) : null}
      </Stack>
    </ApiAndIntegrationsContainer>
  );
};

const ApiAndIntegrationsContainer = styled('div')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 8px;
`;

const SectionTitle = styled('h2')`
  color: ${({ theme }) => theme.colors.basics.black};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const SectionDescription = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const ApiKeysCard = styled(Stack)`
  border: 1px solid ${({ theme }) => theme.colors.gray.c4};
  border-radius: 8px;
  padding: 24px;
`;

const EmptyState = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c10};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 14px;
  margin: 0;
  padding: 32px 0;
  text-align: center;
`;

const ConnectorsGrid = styled('div')`
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr;

  ${mediaQuery('md', 'min')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TableHeaderRow = styled(Flexbox)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.c4};
  padding-bottom: 8px;
`;

const DataRow = styled(Flexbox)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.c3};
  padding: 12px 0;
`;

const HeaderCell = styled('span')<{
  $flex?: number;
  $minWidth?: string;
}>`
  color: ${({ theme }) => theme.colors.gray.c10};
  flex: ${({ $flex }) => ($flex !== undefined ? `${$flex} 1 0` : 'none')};
  font-family: ${({ theme }) => theme.fonts.wallop};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  min-width: ${({ $minWidth }) => $minWidth ?? '0'};
  text-transform: uppercase;
`;

const DataCell = styled('span')<{
  $flex?: number;
  $minWidth?: string;
}>`
  color: ${({ theme }) => theme.colors.gray.c12};
  flex: ${({ $flex }) => ($flex !== undefined ? `${$flex} 1 0` : 'none')};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  min-width: ${({ $minWidth }) => $minWidth ?? '0'};
`;

const MonoCell = styled(DataCell)`
  font-family: monospace;
  overflow-wrap: anywhere;
`;
